import { useState, useEffect } from 'react';
import { DEFAULT_LOCALE, getSafeLocale } from '../utils/locale';
import { IntlProvider } from 'react-intl';
import { LOCALE_SELECTION } from '../constants/storage';
import { loadTranslations } from '../i18n/translations';
import { useLocale, Locale, useCookie } from '@amzn/xb-hooks';

export const PRE_AUTH_LOCALE_COOKIE_NAME = 'preauthLangLocale';

export const useLocaleWrapper = (sourceComponent = 'useLocaleWrapper') => {
  const { locale, setLocale, isLoading, supportedLocales } = useLocale();
  const safeLocale = getSafeLocale(locale, sourceComponent, supportedLocales);
  if (locale?.code !== safeLocale.code) {
    setLocale(safeLocale);
  }

  const setCookie = useCookie(PRE_AUTH_LOCALE_COOKIE_NAME)[1];

  const setLocaleWrapper = (newLocale: Locale) => {
    setLocale(newLocale);
    setCookie(newLocale.code);
    localStorage.setItem(LOCALE_SELECTION, newLocale.code);
  };

  return {
    locale: safeLocale,
    setLocale: setLocaleWrapper,
    isLoading,
    supportedLocales,
  };
};

const LocaleContextProvider = ({ children }: { children: React.ReactNode }) => {
  const { locale } = useLocaleWrapper('LocaleContextProvider');
  const [messages, setMessages] = useState<Record<string, string> | undefined>(
    undefined
  );

  useEffect(() => {
    let cancelled = false;

    loadTranslations(locale.code).then((loaded) => {
      if (!cancelled) {
        setMessages(loaded);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [locale.code]);

  // Always render children — English defaults are used until translations load.
  // This avoids a blank screen if translation loading is slow or fails.
  return (
    <IntlProvider
      defaultLocale={DEFAULT_LOCALE.code}
      locale={locale.code}
      messages={messages}
    >
      {children}
    </IntlProvider>
  );
};

export default LocaleContextProvider;
